import {
  addMonths,
  areIntervalsOverlapping,
  endOfDay,
  format,
  isSameDay,
  isValid,
  parseISO,
  setDate,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

const BOLIVIA_TZ = 'America/La_Paz';

function normalizeFormatTokens(formatStr: string): string {
  return formatStr.replace(/YYYY/g, 'yyyy').replace(/DD/g, 'dd').replace(/YY/g, 'yy');
}

type SupportedDateInput = string | Date | number;

function toDate(input: SupportedDateInput | null | undefined): Date {
  if (input == null || input === '') return new Date(NaN);
  if (input instanceof Date) return input;
  if (typeof input === 'number') return new Date(input);
  const d = parseISO(input);
  return isNaN(d.getTime()) ? new Date(input) : d;
}

function isSupportedDateInput(value: unknown): value is SupportedDateInput {
  return (
    value != null && value !== '' && (typeof value === 'string' || typeof value === 'number' || value instanceof Date)
  );
}

export const getValidDate = (date: unknown, formatStr: string = 'MM/dd/yy HH:mm:ss'): string => {
  if (date == null || date === '') return '';
  if (!isSupportedDateInput(date)) return '';
  const d = toDate(date);
  const safeFormat = normalizeFormatTokens(formatStr);
  return isValid(d) ? format(d, safeFormat) : '';
};

export const bolivianTimeZone = (date?: SupportedDateInput): string => {
  if (date == null) return '';
  const d = toDate(date);
  return isValid(d) ? formatInTimeZone(d, BOLIVIA_TZ, 'dd-MM-yyyy HH:mm:ss') : '';
};

export const formatDateTime = (date: unknown): string => {
  if (date == null || date === '') return '';
  if (!isSupportedDateInput(date)) return '';
  const d = toDate(date);
  return isValid(d) ? format(d, 'dd/MM/yy HH:mm:ss') : '';
};

export const formatDate = (date: SupportedDateInput): string => {
  const d = toDate(date);
  return isValid(d) ? format(d, 'dd/MM/yyyy') : '';
};

export const formatDateDash = (date: SupportedDateInput): string => {
  const d = toDate(date);
  return isValid(d) ? format(d, 'dd-MM-yyyy') : '';
};

export const formatDateTimeDash = (date: SupportedDateInput): string => {
  const d = toDate(date);
  return isValid(d) ? format(d, 'dd-MM-yyyy HH:mm') : '';
};

export const formatDateTimeDashSeconds = (date: SupportedDateInput): string => {
  const d = toDate(date);
  return isValid(d) ? format(d, 'dd-MM-yyyy HH:mm:ss') : '';
};

export const formatDateISO = (date: SupportedDateInput): string => {
  const d = toDate(date);
  return isValid(d) ? format(d, 'yyyy-MM-dd') : '';
};

export const formatDateUS = (date: SupportedDateInput): string => {
  const d = toDate(date);
  return isValid(d) ? format(d, 'MM/dd/yyyy') : '';
};

export const formatDateYearFirst = (date: SupportedDateInput): string => {
  const d = toDate(date);
  return isValid(d) ? format(d, 'yyyy/MM/dd') : '';
};

export const todayForFileName = (): string => format(new Date(), 'yyyy-MM-dd');

export const todayISO = (): string => format(new Date(), 'yyyy-MM-dd');

export const subYearsFromNow = (years: number): Date => subYears(new Date(), years);

export const endOfToday = (): Date => endOfDay(new Date());

export const dateWithDayMonthsAgo = (dayOfMonth: number, monthsAgo: number): Date => {
  const d = new Date();
  const withDay = setDate(d, dayOfMonth);
  return subMonths(withDay, monthsAgo);
};

export const getDateWithDayMonthsAgoISO = (dayOfMonth: number, monthsAgo: number): string =>
  dateWithDayMonthsAgo(dayOfMonth, monthsAgo).toISOString();

export const daysAgo = (days: number, from?: Date): Date => subDays(from ?? new Date(), days);

export const isTodayDate = (date: SupportedDateInput): boolean => {
  const d = toDate(date);
  return isValid(d) && isSameDay(d, new Date());
};

export const formatTime = (date: SupportedDateInput): string => {
  const d = toDate(date);
  return isValid(d) ? format(d, 'HH:mm') : '';
};

export const intervalsOverlap = (start1: Date, end1: Date, start2: Date, end2: Date): boolean =>
  areIntervalsOverlapping({ start: start1, end: end1 }, { start: start2, end: end2 });

export const formatUTC = (date: SupportedDateInput, formatStr: string): string => {
  const d = toDate(date);
  if (!isValid(d)) return '';
  return formatInTimeZone(d, 'UTC', formatStr);
};

export const formatCompact = (date: SupportedDateInput): string => {
  const d = toDate(date);
  return isValid(d) ? format(d, 'yyyyMMdd') : '';
};

export const getFormattedDate = (date?: Date | string): string => {
  let parsedDate: Date;

  if (!date) {
    parsedDate = new Date();
  } else if (typeof date === 'string') {
    parsedDate = parseISO(date);
  } else {
    parsedDate = date;
  }

  if (!isValid(parsedDate)) {
    throw new Error('Invalid date provided');
  }

  return format(parsedDate, 'dd-MM-yyyy HH:mm');
};

export const getCurrentDate = (): string => todayISO();

export const isISODateValid = (dateString: string): boolean => {
  try {
    const parsedDate = parseISO(dateString);
    return isValid(parsedDate);
  } catch {
    return false;
  }
};

export const validateDifferenceInMonths = (start: Date, end: Date, numberOfMonths: number): boolean => {
  if (start && end) {
    const maxEndDate = addMonths(start, numberOfMonths);

    const startDateInMs = maxEndDate.getTime().toString().slice(0, -4);
    const endDateInMs = end.getTime().toString().slice(0, -4);

    if (endDateInMs > startDateInMs) {
      throw new Error('Rango de fechas excedido: 3 meses.');
    }
    return true;
  }
  throw new Error('Error params');
};
